import React, { useEffect, useState } from "react";
import { Box, Button, Divider, Grid, Modal, TextField, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { bankRequisitesService } from "../../../../api/services/bankRequisites";
import { useSnack } from "../../../../utils/useSnack";
import { Company } from "../../../../types/company";
import { State } from "../../../../redux/store";
import { useTranslation } from "react-i18next";
import { modalStyle } from "../../../../common/modalStyle";
import { BankRequisites } from "../../../../types/bankRequisites";

interface BankRequisiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editMode?: boolean;
  requisite?: BankRequisites;
}

export default function BankRequisiteModal({
  isOpen,
  onClose,
  onSuccess,
  editMode = false,
  requisite
}: BankRequisiteModalProps) {
  const { t } = useTranslation();
  const { snack } = useSnack();

  const state = useSelector((state: State) => state);
  const company: Company | null = state.company.company;

  const [bik, setBik] = useState("");
  const [bikError, setBikError] = useState("");
  
  const [iik, setIik] = useState("");
  const [iikError, setIikError] = useState("");
  
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");

  const [highlight, setHighlight] = useState(false);

  useEffect(() => {
    if (editMode && requisite) {
      setBik(requisite.bik);
      setIik(requisite.iik);
      setName(requisite.name);
    }
  }, [editMode, requisite]);

  const onCancel = () => {
    onClose();
    setBik("");
    setIik("");
    setName("");
    setHighlight(false);
    setBikError("");
    setIikError("");
    setNameError("");
  };

  const validateForm = () => {
    let isValid = true;
    
    if (!bik) {
      setBikError(t("validation.bik_required"));
      isValid = false;
    }
    
    if (!iik) {
      setIikError(t("validation.iik_required"));
      isValid = false;
    }
    
    if (!name) {
      setNameError(t("validation.bank_name_required"));
      isValid = false;
    }
    
    return isValid;
  };

  const onSubmit = async () => {
    if (!company) return;

    setHighlight(true);
    
    if (validateForm()) {
      try {
        let response;
        
        if (editMode && requisite) {
          response = await bankRequisitesService.updateBankRequisite(
            company.id,
            requisite.id,
            { bik, iik, name }
          );
        } else {
          response = await bankRequisitesService.createBankRequisite(
            company.id,
            { bik, iik, name }
          );
        }

        if (response.status === "success") {
          snack(
            editMode
              ? t("snack.success.bank_requisite_updated")
              : t("snack.success.bank_requisite_added"),
            "success"
          );
          onCancel();
          onSuccess();
        } else {
          snack(
            editMode
              ? t("snack.error.update_bank_requisite")
              : t("snack.error.add_bank_requisite"),
            "error"
          );
        }
      } catch (err) {
        snack(
          editMode
            ? t("snack.error.update_bank_requisite")
            : t("snack.error.add_bank_requisite"),
          "error"
        );
      }
    } else {
      snack(t("snack.warning.empty_fields"), "warning");
    }
  };

  useEffect(() => {
    if (bikError) setBikError("");
  }, [bik]);
  
  useEffect(() => {
    if (iikError) setIikError("");
  }, [iik]);
  
  useEffect(() => {
    if (nameError) setNameError("");
  }, [name]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography
          component="h5"
          variant="h5"
          sx={{ p: 2, fontWeight: 700, fontSize: { xs: 14, sm: 20 } }}
        >
          {editMode
            ? t("cabinet.bank_requisites.edit_bank_requisite")
            : t("cabinet.bank_requisites.add_bank_requisite")}
        </Typography>

        <Divider sx={{ mb: 1.5, ml: "auto", mr: "auto", width: "100%" }} />

        <Grid container style={{ flexDirection: "column" }} sx={{ p: 2 }}>
          <Grid item>
            <Grid item sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label={t("cabinet.bank_requisites.bik")}
                name="bik"
                color="primary"
                value={bik}
                onChange={(e) => setBik(e.target.value)}
                error={highlight && !!bikError}
                helperText={highlight && bikError}
              />
            </Grid>

            <Grid item sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label={t("cabinet.bank_requisites.iik")}
                name="iik"
                color="primary"
                value={iik}
                onChange={(e) => setIik(e.target.value)}
                error={highlight && !!iikError}
                helperText={highlight && iikError}
              />
            </Grid>

            <Grid item>
              <TextField
                fullWidth
                label={t("cabinet.bank_requisites.bank_name")}
                name="name"
                color="primary"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={highlight && !!nameError}
                helperText={highlight && nameError}
              />
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ mt: 1.5, ml: "auto", mr: "auto", width: "100%" }} />

        <Grid item container spacing={2} justifyContent="flex-end" sx={{ p: 2 }}>
          <Grid item xs="auto">
            <Button
              fullWidth
              color="secondary"
              variant="contained"
              onClick={onCancel}
            >
              {t("cabinet.bank_requisites.cancel")}
            </Button>
          </Grid>
          <Grid item xs="auto">
            <Button
              fullWidth
              color="primary"
              variant="contained"
              onClick={onSubmit}
            >
              {editMode
                ? t("cabinet.bank_requisites.save")
                : t("cabinet.bank_requisites.add")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}